import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  Checkbox,
  TextField,
} from "@mui/material";
import { useState } from "react";
import theme from "../../utils/theme";
import ImagePreviewModal from "./ImagePreviewModal";
import IconButtons from "./IconButtons";

const styles = {
  checkbox: {
    width: 42,
    height: 42,
  },
  tableContainer: {
    borderRadius: "0 0 8px 8px",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.05)",
    border: `${theme.palette.grey[300]} 1.5px solid`,
    overflowX: "auto",
  },
  headerBox: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    p: 1,
    bgcolor: theme.palette.background.dark,
    minHeight: 56,
    borderRadius: "8px 8px 0 0",
  },
  lightHeaderBox: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    p: 1,
    bgcolor: theme.palette.grey[300],
    minHeight: 56,
    borderRadius: "8px 8px 0 0",
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.light,
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  lightTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  actionButtonsContainer: {
    display: "flex",
    gap: 1,
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: "1rem",
    borderBottom: "2px solid #e0e0e0",
    bgcolor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    py: 1.5,
  },
  tableRow: {
    bgcolor: "#fafbfc",
    transition: "background 0.2s",
    "&:hover": {
      bgcolor: "#e9f4ff",
    },
    "&:last-child td, &:last-child th": { border: 0 },
  },
  tableCell: {
    verticalAlign: "top",
  },
  oddTableRow: {
    bgcolor: "#f3f4f6",
  },
  // Style for stage fields text inputs
  textField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[500],
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: theme.palette.grey[500],
        },
      },
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.grey[500],
    },
  },
  itemNameCell: {
    verticalAlign: "top",
    // borderRight: "1px solid #e0e0e0",
    bgcolor: "#f8f9fa",
  },
};

// Define table columns as a constant array
const TABLE_COLUMNS = [
  { id: "item", label: "Item", width: "15%" },
  { id: "description", label: "Description", width: "30%" },
  { id: "specification", label: "Specification" },
  { id: "tolerance", label: "Tolerance" },
];

// Define stage fields columns
const STAGE_COLUMNS = [
  {
    id: "inspection",
    label: "Actual inspection by QC Inspector",
    cellSize: "large",
  },
  { id: "finish", label: "Finish Process" },
  { id: "remark", label: "Remark", cellSize: "large" },
];

// Helper component for rendering stage fields
const StageFieldsCell = ({
  subpart,
  fieldType,
  fieldName,
  formik,
  disabled,
}) => {
  const fieldId = `subpart_${subpart.subpart_id}_${fieldName}`;

  if (fieldType === "checkbox") {
    return (
      <Checkbox
        id={fieldId}
        name={fieldId}
        checked={Boolean(formik.values[fieldId])}
        onChange={(e) => {
          formik.setFieldValue(fieldId, e.target.checked);
        }}
        onBlur={formik.handleBlur}
        sx={styles.checkbox}
        disabled={disabled}
      />
    );
  } else {
    return (
      <TextField
        id={fieldId}
        name={fieldId}
        type={fieldName === "actual_inspection" ? "number" : "text"}
        value={formik.values[fieldId] || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        variant="outlined"
        sx={styles.textField}
        error={formik.touched[fieldId] && Boolean(formik.errors[fieldId])}
        helperText={formik.touched[fieldId] && formik.errors[fieldId]}
        fullWidth
        disabled={disabled}
      />
    );
  }
};

const DimensionTable = ({
  items,
  component_name,
  images = [],
  onEdit,
  onDelete,
  showStagesFields = false,
  disableDelete = false,
  formik,
  disabled = false,
  lightHeader = false,
}) => {
  const [carouselOpen, setCarouselOpen] = useState(false);

  if (!items || items.length === 0) return null;

  return (
    <>
      <ImagePreviewModal
        images={images.map((image) => image.image_url)}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
      />
      <Box sx={lightHeader ? styles.lightHeaderBox : styles.headerBox}>
        <Typography
          variant="subtitle1"
          sx={lightHeader ? styles.lightTitle : styles.title}
        >
          Dimensions for {component_name || "Component"}
        </Typography>

        {images && images.length > 0 && (
          <Tooltip title={`View Images`} arrow placement="top">
            <IconButtons
              onClick={() => setCarouselOpen(true)}
              buttonType="view"
              aria-label="View Images"
              sx={{ mr: 1 }}
            />
          </Tooltip>
        )}

        {onEdit && onDelete && (
          <Box sx={styles.actionButtonsContainer}>
            <Tooltip title={`Edit`} arrow placement="top">
              <IconButtons
                onClick={onEdit}
                buttonType="edit"
                aria-label="Edit component"
                disabled={disabled}
              />
            </Tooltip>
            <Tooltip title={`Delete`} arrow placement="top">
              <IconButtons
                onClick={onDelete}
                disabled={disableDelete}
                buttonType="delete"
                aria-label="Delete component"
              />
            </Tooltip>
          </Box>
        )}
      </Box>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table aria-label="dimension table">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
              {/* Render standard columns */}
              {TABLE_COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ ...styles.tableHeadCell, width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}

              {/* Render stage fields columns if enabled */}
              {showStagesFields &&
                STAGE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      ...styles.tableHeadCell,
                      minWidth: column.cellSize === "large" ? "220px" : "70px",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map((group, groupIdx) => {
              const validSubparts = (
                Array.isArray(group?.sub_parts) ? group.sub_parts : []
              ).filter(
                (subpart) =>
                  subpart?.description?.trim() !== "" ||
                  subpart?.specification?.trim() !== "" ||
                  subpart?.tolerance?.trim() !== ""
              );

              if (validSubparts?.length === 0) return null;

              return validSubparts.map((subpart, subpartIdx) => (
                <TableRow
                  key={`${groupIdx}-${subpartIdx}`}
                  sx={{
                    ...styles.tableRow,
                    ...(groupIdx % 2 !== 0 && styles.oddTableRow),
                  }}
                >
                  {/* Item Name Cell - only show for first subpart */}
                  {subpartIdx === 0 ? (
                    <TableCell
                      rowSpan={validSubparts.length}
                      sx={{
                        ...styles.itemNameCell,
                        py: 2,
                        ...styles.tableCell,
                      }}
                    >
                      <Typography variant="body2">{group.item_name}</Typography>
                    </TableCell>
                  ) : null}

                  {/* Description Cell */}
                  <TableCell sx={{ py: 2, ...styles.tableCell }}>
                    <Typography variant="body2">
                      {subpart?.description?.trim() !== ""
                        ? subpart.description
                        : "\u00A0"}
                    </Typography>
                  </TableCell>

                  {/* Specification Cell */}
                  <TableCell sx={{ py: 2, ...styles.tableCell }}>
                    <Typography variant="body2">
                      {subpart?.specification?.trim() !== ""
                        ? subpart.specification
                        : "\u00A0"}
                    </Typography>
                  </TableCell>

                  {/* Tolerance Cell */}
                  <TableCell sx={{ py: 2, ...styles.tableCell }}>
                    <Typography variant="body2">
                      {subpart?.tolerance?.trim() !== ""
                        ? subpart.tolerance
                        : "\u00A0"}
                    </Typography>
                  </TableCell>

                  {/* Additional fields if showStagesFields is true */}
                  {showStagesFields && (
                    <>
                      {/* Inspection column */}
                      <TableCell sx={{ py: 2, ...styles.tableCell }}>
                        <StageFieldsCell
                          subpart={subpart}
                          fieldType="number"
                          fieldName="actual_inspection"
                          formik={formik}
                          disabled={disabled}
                        />
                      </TableCell>

                      {/* Finish Process column */}
                      <TableCell sx={{ py: 2, ...styles.tableCell }}>
                        <StageFieldsCell
                          subpart={subpart}
                          fieldType="checkbox"
                          fieldName="finish_process"
                          formik={formik}
                          disabled={disabled}
                        />
                      </TableCell>

                      {/* Remark column */}
                      <TableCell sx={{ py: 2, ...styles.tableCell }}>
                        <StageFieldsCell
                          subpart={subpart}
                          fieldType="text"
                          fieldName="remark"
                          formik={formik}
                          disabled={disabled}
                        />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DimensionTable;
